import { faCheck, faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Button,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Popover,
  PopoverBody,
  PopoverHeader,
  UncontrolledPopover,
} from 'reactstrap';
import Swal from 'sweetalert2';
import { useAuth0 } from '../../../../../auth0/reactAuth0Spa';
import { checkLoginForError, formatBearerToken } from '../../../../../Common.functions';
import { DepartmentContext } from '../../../../../DepartmentWrapper';
import { deleteData, postDataAgnostic, putData } from '../../../../../Services/dataApi';
import AddTimeSlotPopover from '../AddTimeSlotPopover';
import '../Schedule.style.css';
import style from './style.js';

const TimeSlotHeaderPopover = ({
  scheduleData,
  fetchSchedule,
  hideHeaderPopover,
  updateAllData,
  visibility = {},
  setTimeSlotHeaderPopoverChildVisibility,
  requestCloseTimeSlotHeaderPopover,
  forceCloseTimeSlotHeaderPopover,
  forcedContext,
  pk_InterviewDate,
  selectedHeader,
  dateIsInThePast,
  willEditThePast,
  fireConfirmationForEditingPastSchedule,
  VirtualMeetingMode,
  onChangeDetected,
  unsavedPromptOpenRef,
  onScheduleTimeShift,
}) => {
  const { metaData } = scheduleData;
  const containerRef = useRef(null);
  const addTimeSlotPopoverRef = useRef(null);
  const [standardDuration, setStandardDuration] = useState(1);
  const [standardPassingDuration, setStandardPassingDuration] = useState(1);
  const [startDateAndTime, setStartDateAndTime] = useState();
  const [startTime, setStartTime] = useState();

  // Track original values for change detection
  const [originalStandardDuration, setOriginalStandardDuration] = useState(1);
  const [originalStandardPassingDuration, setOriginalStandardPassingDuration] = useState(1);
  const [originalStartTime, setOriginalStartTime] = useState();
  const [nestedAddTimeSlotHasChanges, setNestedAddTimeSlotHasChanges] = useState(false);

  // Alert functionality replaced with toast
  const history = useHistory();
  const deptContext = useContext(DepartmentContext);
  const dContext = forcedContext ? forcedContext : deptContext;
  const embeddedMeetingsEnabled = VirtualMeetingMode === 'Internal' && Boolean(dContext?.department?.enableEmbeddedMeetings);
  const enforce35MinuteLimit = VirtualMeetingMode === 'Internal' && !embeddedMeetingsEnabled;

  const { getTokenSilently, loginWithRedirect } = useAuth0();
  const showAddTimeSlotPopover = Boolean(visibility.showAddTimeSlotPopover);
  const showConfirmModal = Boolean(visibility.showConfirmModal);

  useEffect(() => {
    const { StandardDurationInMinutes, StandardPassingDurationInMinutes, DateOfInterview, StartTime } = metaData || {};
    const formattedStartTime = dayjs(`${DateOfInterview} ${StartTime}`).format('HH:mm');

    setStandardDuration(StandardDurationInMinutes);
    setStandardPassingDuration(StandardPassingDurationInMinutes);
    setStartDateAndTime(`${DateOfInterview} ${StartTime}`);
    setStartTime(formattedStartTime);

    // Store original values for change detection
    setOriginalStandardDuration(StandardDurationInMinutes);
    setOriginalStandardPassingDuration(StandardPassingDurationInMinutes);
    setOriginalStartTime(formattedStartTime);
  }, []);

  // Detect changes in form fields
  useEffect(() => {
    const startTimeChanged = startTime !== originalStartTime;
    const durationChanged = standardDuration !== originalStandardDuration;
    const passingChanged = standardPassingDuration !== originalStandardPassingDuration;
    const hasChanges = startTimeChanged || durationChanged || passingChanged || nestedAddTimeSlotHasChanges;

    if (onChangeDetected) {
      onChangeDetected(hasChanges);
    }
  }, [
    startTime,
    originalStartTime,
    standardDuration,
    originalStandardDuration,
    standardPassingDuration,
    originalStandardPassingDuration,
    nestedAddTimeSlotHasChanges,
    onChangeDetected,
  ]);

  const isWithinTarget = (target, id) => {
    if (typeof document === 'undefined') {
      return false;
    }

    const element = document.getElementById(id);
    return Boolean(element && element.contains(target));
  };

  const closeAddTimeSlotPopover = async () => {
    if (!showAddTimeSlotPopover) {
      return true;
    }

    if (nestedAddTimeSlotHasChanges) {
      if (unsavedPromptOpenRef?.current) {
        return false;
      }
      if (unsavedPromptOpenRef) {
        unsavedPromptOpenRef.current = true;
      }
      const result = await Swal.fire({
        title: 'Unsaved Changes',
        text: 'You have unsaved changes. Do you want to discard them?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Discard Changes',
        cancelButtonText: 'Keep Editing',
      });
      if (unsavedPromptOpenRef) {
        unsavedPromptOpenRef.current = false;
      }
      if (!result.isConfirmed) {
        return false;
      }
      setNestedAddTimeSlotHasChanges(false);
    }

    setTimeSlotHeaderPopoverChildVisibility('showAddTimeSlotPopover', false);
    return true;
  };

  const openAddTimeSlotPopover = async () => {
    if (dateIsInThePast && !willEditThePast) {
      const continueProcess = await fireConfirmationForEditingPastSchedule();
      if (!continueProcess) {
        return false;
      }
    }

    setTimeSlotHeaderPopoverChildVisibility('showAddTimeSlotPopover', true);
    return true;
  };

  const toggleAddTimeSlotPopover = async (e) => {
    e?.stopPropagation?.();

    if (showAddTimeSlotPopover) {
      await closeAddTimeSlotPopover();
      return;
    }

    await openAddTimeSlotPopover();
  };

  useEffect(() => {
    const handleDocumentMouseDown = (e) => {
      if (unsavedPromptOpenRef?.current) {
        return;
      }

      if (showConfirmModal) {
        return;
      }

      const target = e.target;
      const clickedInsideParentContent = Boolean(containerRef.current && containerRef.current.contains(target));
      const clickedParentTarget = isWithinTarget(target, 'timeSlotHeader');
      const clickedChildTarget = isWithinTarget(target, 'header_timeSlot');
      const clickedChildPopover = Boolean(addTimeSlotPopoverRef.current && addTimeSlotPopoverRef.current.contains(target));

      if (clickedChildTarget || clickedChildPopover) {
        return;
      }

      if (showAddTimeSlotPopover) {
        void closeAddTimeSlotPopover();
        return;
      }

      if (!clickedInsideParentContent && !clickedParentTarget) {
        void requestCloseTimeSlotHeaderPopover();
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown, true);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown, true);
    };
  }, [requestCloseTimeSlotHeaderPopover, showAddTimeSlotPopover, showConfirmModal, unsavedPromptOpenRef]);

  const renderAddTimeSlotPopOver = () => {
    return (
      <Popover
        placementPrefix="addTimeSlotPopover bs-popover" // Change with room id later
        target={`header_timeSlot`} // Change with room id later
        trigger="manual"
        innerRef={(node) => {
          addTimeSlotPopoverRef.current = node;
        }}
        isOpen={showAddTimeSlotPopover}
        toggle={() => {}}
      >
        <PopoverHeader>
          <div style={style.buttonLabel}> Add time slot/s to schedule</div>
        </PopoverHeader>
        <PopoverBody>
          <AddTimeSlotPopover
            fetchSchedule={fetchSchedule}
            scheduleData={scheduleData}
            hideHeaderPopover={hideHeaderPopover}
            setShowAddTimeSlotPopover={(isOpen) =>
              setTimeSlotHeaderPopoverChildVisibility('showAddTimeSlotPopover', isOpen)
            }
            isOpen={showAddTimeSlotPopover}
            onChangeDetected={setNestedAddTimeSlotHasChanges}
            onScheduleTimeShift={onScheduleTimeShift}
          />
        </PopoverBody>
      </Popover>
    );
  };

  const save = () => {
    // Reset change flag before saving
    if (onChangeDetected) {
      onChangeDetected(false);
    }

    const { department, season } = dContext;
    const pathExploded = history.location.pathname.split('/');
    const scheduleID = pk_InterviewDate ? pk_InterviewDate : pathExploded[pathExploded.length - 1];
    getTokenSilently()
      .then((token) => {
        putData(
          'department/interviewDate/putEntry',
          {
            pk_Department: department.pk_Department,
            pk_InterviewDate: scheduleID,
            pk_Season: season.pk_Season,
          },
          {
            entry: {
              StartTime: startTime,
              StandardDurationInMinutes: standardDuration,
              StandardPassingDurationInMinutes: standardPassingDuration,
            },
          },
          formatBearerToken(token),
        )
          .then((res) => {
            setOriginalStandardDuration(standardDuration);
            setOriginalStandardPassingDuration(standardPassingDuration);
            setOriginalStartTime(startTime);
            setNestedAddTimeSlotHasChanges(false);
            if (onChangeDetected) {
              onChangeDetected(false);
            }
            updateAllData();
            if (forceCloseTimeSlotHeaderPopover) {
              forceCloseTimeSlotHeaderPopover();
            } else {
              hideHeaderPopover();
            }
          })
          .catch((err) => {});
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  /**Deletes all rows and columns */
  const clearSchedule = () => {
    const { department, season } = dContext;
    const pathExploded = history.location.pathname.split('/');
    const scheduleID = pk_InterviewDate ? pk_InterviewDate : pathExploded[pathExploded.length - 1];
    getTokenSilently()
      .then((token) => {
        deleteData(
          'department/season/interviewDate',
          {
            pk_Department: department.pk_Department,
            pk_InterviewDate: Number(scheduleID),
            pk_Season: season.pk_Season,
          },
          null,
          formatBearerToken(token),
        )
          .then((res) => {
            updateAllData();
            hideHeaderPopover();
            toast.success('Schedule cleared!');
          })
          .catch((err) => {
            toast.error('Delete schedule error!');
          });
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  return (
    <div ref={containerRef} style={style.mainBody}>
      <FormGroup style={{ width: '100%', paddingLeft: 10, paddingRight: 10 }}>
        <Label style={style.inputLabel} for="startTime">
          Start Time
        </Label>
        <Input
          type="time"
          name="startTime"
          id="startTime"
          value={startTime || ''}
          onClick={async (e) => {
            if (dateIsInThePast && !willEditThePast) {
              // e.preventDefault();
              const continueProcess = await fireConfirmationForEditingPastSchedule();
              if (!continueProcess) {
                return;
              }
            }
          }}
          onChange={async (e) => {
            // deconstructing this value is a necessity, otherwise the event target might change while waiting for the async
            // function to resolve
            const { value } = e.target;
            if (dateIsInThePast && !willEditThePast) {
              const continueProcess = await fireConfirmationForEditingPastSchedule();
              if (!continueProcess) {
                return;
              }
            }
            setStartTime(value);
          }}
          placeholder="Enter Time. . ."
        />
      </FormGroup>
      <FormGroup
        style={{ width: '100%', paddingLeft: 10, paddingRight: 10 }}
        onClick={async () => {
          if (dateIsInThePast && !willEditThePast) {
            const continueProcess = await fireConfirmationForEditingPastSchedule();
            if (!continueProcess) {
              return;
            }
          }
        }}
      >
        <Label style={style.inputLabel} for="interviewDuration">
          Interview Duration
        </Label>
        <InputGroup>
          <Input
            type="number"
            style={style.inputFields}
            name="interviewDuration"
            id="interviewDuration"
            value={standardDuration}
            placeholder="Enter Duration. . ."
            onChange={(e) => {
              const newValue = parseInt(e.target.value) >= 0 ? parseInt(e.target.value) : 0;
              setStandardDuration(newValue);
            }}
          />
          <InputGroupText>minutes</InputGroupText>
        </InputGroup>
      </FormGroup>
      <FormGroup
        style={{ width: '100%', paddingLeft: 10, paddingRight: 10 }}
        onClick={async () => {
          if (dateIsInThePast && !willEditThePast) {
            const continueProcess = await fireConfirmationForEditingPastSchedule();
            if (!continueProcess) {
              return;
            }
          }
        }}
      >
        <Label style={style.inputLabel} for="passingPeriod">
          Passing Period
        </Label>
        <InputGroup>
          <Input
            type="number"
            style={style.inputFields}
            name="passingPeriod"
            id="passingPeriod"
            value={standardPassingDuration}
            placeholder="Enter Passing Period. . ."
            onChange={(e) => {
              const newValue = parseInt(e.target.value) >= 0 ? parseInt(e.target.value) : 0;
              setStandardPassingDuration(newValue);
            }}
          />
          <InputGroupText>minutes</InputGroupText>
        </InputGroup>
      </FormGroup>
      <div>
        <div style={style.inputLabel}>Block Duration: {`${standardDuration + standardPassingDuration} minutes`}</div>
        {enforce35MinuteLimit && standardDuration + standardPassingDuration > 35 && (
          <div style={{ color: 'red', fontSize: '0.875rem', marginTop: 4 }}>Must not be more than 35mins</div>
        )}
      </div>
      <br />
      <div style={{ ...style.simpleRow, width: '100%' }}>
        <Button
          disabled={enforce35MinuteLimit && standardDuration + standardPassingDuration > 35}
          style={{ ...style.genButton, backgroundColor: 'var(--rr-primary-strong)', color: 'var(--rr-on-primary)' }}
          onClick={async () => {
            if (dateIsInThePast && !willEditThePast) {
              const continueProcess = await fireConfirmationForEditingPastSchedule();
              if (!continueProcess) {
                return;
              }
            }
            const data = {
              standardDuration,
              standardPassingDuration,
              startTime,
            };

            save(data);
          }}
        >
          <div style={style.spaceBetweenRow}>
            <FontAwesomeIcon icon={faCheck} style={style.buttonIcon} />
            <div style={style.buttonLabel}>Save</div>
          </div>
        </Button>
      </div>
      <div style={{ ...style.simpleRow, width: '100%' }}>
        <Button id="header_timeSlot" color="success" style={style.genButton} onClick={toggleAddTimeSlotPopover}>
          <div style={style.spaceBetweenRow}>
            <FontAwesomeIcon icon={faPlus} style={style.buttonIcon} />
            <div style={style.buttonLabel}>Insert Time Slot(s)</div>
          </div>
        </Button>
      </div>
      <div style={{ ...style.simpleRow, width: '100%' }}>
        <Button
          color="danger"
          style={{ ...style.genButton, backgroundColor: null }}
          onClick={async () => {
            if (dateIsInThePast && !willEditThePast) {
              const continueProcess = await fireConfirmationForEditingPastSchedule();
              if (!continueProcess) {
                return;
              }
            }
            setTimeSlotHeaderPopoverChildVisibility('showConfirmModal', true);
            Swal.fire({
              title: 'Are you sure you want to delete all time slots and evaluator rooms',
              text: "You won't be able to revert this!",
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#d33',
              cancelButtonColor: 'gray',
              confirmButtonText: 'Delete Schedule',
            }).then((result) => {
              if (result.isConfirmed) {
                clearSchedule();
                // DELETE API here
              } else {
              }

              setTimeSlotHeaderPopoverChildVisibility('showConfirmModal', false);
            });
          }}
        >
          <div style={style.spaceBetweenRow}>
            <FontAwesomeIcon icon={faTrashAlt} style={style.buttonIcon} />
            <div style={style.buttonLabel}>Delete Entire Schedule</div>
          </div>
        </Button>
      </div>
      {renderAddTimeSlotPopOver()}
    </div>
  );
};

export default TimeSlotHeaderPopover;
